'use client'

import { useEffect, useState } from 'react'

export type TocItem = {
  /** Section number, rendered as a zero-padded rank (01, 02, …). Optional. */
  n?: number
  title: string
  /** In-page anchor, e.g. `#s3`. Must match the section's id. */
  href: string
}

/**
 * Sticky "on this page" rail for the editorial legal-policy pages.
 *
 * - Desktop (lg+): a sticky vertical nav with scroll-spy. The section the
 *   reader is currently in is highlighted via IntersectionObserver.
 * - Mobile / tablet (<lg): a collapsed <details> "On this page" disclosure
 *   that stacks above the prose. No JS needed to open it; the scroll-spy
 *   highlight is a desktop-only enhancement.
 *
 * Pure progressive enhancement: with JS off, every entry is still a working
 * anchor link.
 */
export default function TocRail({
  items,
  label = 'On this page',
}: {
  items: TocItem[]
  label?: string
}) {
  const [active, setActive] = useState<string>('')

  useEffect(() => {
    const ids = items.map((i) => i.href.replace(/^#/, ''))
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el != null)
    if (els.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const onscreen = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (onscreen[0]) setActive(onscreen[0].target.id)
      },
      // Trip the active section once its heading passes ~96px below the top
      // (clearing the sticky nav) and before it leaves the upper third.
      { rootMargin: '-96px 0px -68% 0px', threshold: 0 },
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [items])

  if (items.length === 0) return null

  const list = (
    <ol className="space-y-1">
      {items.map((item) => {
        const id = item.href.replace(/^#/, '')
        const isActive = id === active
        return (
          <li key={item.href}>
            <a
              href={item.href}
              aria-current={isActive ? 'true' : undefined}
              className={[
                'group flex items-baseline gap-2.5 rounded-md px-2 py-1.5 text-body-sm leading-snug transition-colors',
                isActive
                  ? 'text-terra-600 bg-terra-500/[0.07]'
                  : 'text-ink-600 hover:text-ink-900 hover:bg-ink-900/[0.04]',
              ].join(' ')}
            >
              {item.n != null && (
                <span
                  className={[
                    'mono text-eyebrow tabular-nums shrink-0 pt-px',
                    isActive ? 'text-terra-500' : 'text-ink-400 group-hover:text-ink-600',
                  ].join(' ')}
                >
                  {String(item.n).padStart(2, '0')}
                </span>
              )}
              <span className="min-w-0">{item.title}</span>
            </a>
          </li>
        )
      })}
    </ol>
  )

  return (
    <>
      {/* Mobile / tablet: collapsible disclosure above the prose. */}
      <details className="lg:hidden mb-10 rounded-xl border border-ink-900/10 bg-cream-50/60 px-4 py-3">
        <summary className="mono text-eyebrow uppercase tracking-widest text-ink-600 cursor-pointer select-none">
          {label}
        </summary>
        <nav aria-label={label} className="mt-3">
          {list}
        </nav>
      </details>

      {/* Desktop: sticky scroll-spy rail. */}
      <nav
        aria-label={label}
        className="hidden lg:block"
      >
        <p className="mono text-eyebrow uppercase tracking-widest text-ink-500 mb-3 px-2">
          {label}
        </p>
        {list}
      </nav>
    </>
  )
}
